/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography, SkeletonText, Link } from '@zapier/design-system';

import { MEDIA_MIN_WIDTH } from '../../constants';
import { slugify } from '../../utils/slugify';
import { RichTextHTMLString } from '../sections/types';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { Department as DepartmentData, useDepartments } from './data';
import { Fragment } from 'react';

export interface JobBoardProps {
  title?: string;
  description?: RichTextHTMLString;
}

interface DepartmentProps extends Omit<DepartmentData, 'id' | 'children'> {
  subDepartments: DepartmentData[];
  level: number;
}

const Styles = labelStyles('JobBoard', {
  root: css({
    maxWidth: '1170px',
    margin: '0 auto',
    padding: '20px',

    [MEDIA_MIN_WIDTH.small]: {
      padding: '40px 20px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      padding: '60px 20px',
    },
  }),

  title: css({
    ...Typography.pageHeader9,
    color: Colors.neutral800,

    [MEDIA_MIN_WIDTH.small]: {
      ...Typography.pageHeader7,
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.pageHeader5,
    },
  }),

  description: css({
    ...Typography.paragraph3,
    color: Colors.neutral800,
    maxWidth: '600px',
    marginTop: '12px',

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.paragraph1,
      marginTop: '20px',
    },
  }),

  departmentList: css({
    margin: 0,
    listStyleType: 'none',
  }),

  department: (level = 0) =>
    css(
      level === 0
        ? {
            marginTop: '32px',

            [MEDIA_MIN_WIDTH.large]: {
              marginTop: '48px',
            },
          }
        : {
            paddingTop: '16px',
            paddingLeft: `${level * 24}px`,
            borderTop: `1px solid ${Colors.neutral300}`,
          },
    ),

  departmentHeading: (level = 0) =>
    css({
      color: Colors.neutral800,

      ...(level === 0
        ? {
            ...Typography.paragraph1Bold,

            [MEDIA_MIN_WIDTH.large]: {
              ...Typography.sectionHeader3Bold,
            },
          }
        : {
            ...Typography.paragraph3Bold,

            [MEDIA_MIN_WIDTH.large]: {
              ...Typography.paragraph1Bold,
              fontSize: '20px',
            },
          }),
    }),

  jobList: (level = 0) =>
    css({
      margin: '12px 0 0 0',
      listStyleType: 'none',
      ...(level === 0 && { borderBottom: `1px solid ${Colors.neutral300}` }),

      [MEDIA_MIN_WIDTH.large]: {
        marginTop: '16px',
      },
    }),

  job: css({
    padding: '16px 0',
    borderTop: `1px solid ${Colors.neutral300}`,
  }),

  jobLink: css({
    ...Typography.paragraph3,
    color: Colors.blue,
    textDecoration: 'underline',

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.paragraph1,
    },

    '&:hover': {
      color: Colors.night,
    },

    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
      outlineOffset: '2px',
    },
  }),
});

function Department(props: DepartmentProps) {
  if (
    props.jobs.length === 0 &&
    props.subDepartments.every((subDepartment) => {
      return subDepartment.jobs.length === 0;
    })
  ) {
    return null;
  }

  const Heading = `h${Math.min(props.level + 3, 6)}` as
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';

  return (
    <li css={Styles.department(props.level)}>
      <Heading css={Styles.departmentHeading(props.level)}>
        {props.name}
      </Heading>
      <ul css={Styles.jobList(props.level)}>
        {props.jobs.map((job) => (
          <li css={Styles.job} key={job.id}>
            <a
              css={Styles.jobLink}
              href={`/jobs/${job.id}/${slugify(job.title)}`}
            >
              {job.title}
            </a>
          </li>
        ))}
        {props.subDepartments && props.subDepartments.length > 0 && (
          <li>
            <ul css={Styles.departmentList}>
              {props.subDepartments.map((subDepartment) => (
                <Department
                  key={subDepartment.id}
                  level={props.level + 1}
                  name={subDepartment.name}
                  jobs={subDepartment.jobs}
                  subDepartments={subDepartment.children}
                />
              ))}
            </ul>
          </li>
        )}
      </ul>
    </li>
  );
}

function JobListings() {
  const { departments, isLoading, hasError } = useDepartments();

  if (isLoading)
    return (
      <Fragment>
        {Array.from({ length: 13 }).map((_, index) => (
          <div key={index} css={Styles.department()}>
            <SkeletonText size="heading" lines={1} />
            <SkeletonText size="body" lines={3} />
          </div>
        ))}
      </Fragment>
    );

  if (hasError)
    return (
      <div css={Styles.department()}>
        <h3 css={Styles.departmentHeading()}>
          Something went wrong. Please try again later.
        </h3>
      </div>
    );

  if (departments.length === 0)
    return (
      <div css={Styles.department()}>
        <h3 css={Styles.departmentHeading()}>
          We don't currently have any open positions.
        </h3>
        <p css={Styles.description}>
          However, we are always on the lookout for talented, passionate people
          who want to work with us, so{' '}
          <Link href="mailto:jobs@zapier.com" title="Email jobs@zapier.com">
            get in touch
          </Link>{' '}
          with what your dream role would be at Zapier.
        </p>
      </div>
    );

  return (
    <ul css={Styles.departmentList}>
      {departments.map((department) => (
        <Department
          key={department.id}
          level={0}
          name={department.name}
          jobs={department.jobs}
          subDepartments={department.children}
        />
      ))}
    </ul>
  );
}

export function JobBoard(props: JobBoardProps) {
  return (
    <div css={Styles.root}>
      {props.title && <h2 css={Styles.title}>{props.title}</h2>}
      {props.description && (
        <div css={Styles.description}>
          <RichTextHtml html={props.description} />
        </div>
      )}
      <JobListings />
    </div>
  );
}
